import { type ReactNode, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { EChartsCoreOption } from "echarts/core";
import type { ScheduleOut } from "../api/generated/model";
import {
  getGetJobV1JobsJobIdGetQueryKey,
  getListJobsV1JobsGetQueryKey,
  getListRunsV1RunsGetQueryKey,
  getListSchedulesV1JobsJobIdSchedulesGetQueryKey,
  runSweepStatsBatchV1RunsSweepStatsBatchPost,
  useDeleteJobV1JobsJobIdDelete,
  useGetJobV1JobsJobIdGet,
  useListRunsV1RunsGet,
  useListSchedulesV1JobsJobIdSchedulesGet,
  useRunNowV1JobsJobIdRunsPost,
  useUpdateJobV1JobsJobIdPatch,
} from "../api/generated/qcsControlPlane";
import {
  type Flow,
  type Job,
  type Run,
  ago,
  elapsedSeconds,
  fmtDateTime,
  fmtDuration,
  fmtInt,
  fmtRows,
  flowLabel,
  isTerminal,
  parseTs,
  platformLabel,
  probeThroughput,
  statusTone,
  throughput,
} from "../api/labels";
import { JobForm, humanizeCron } from "./Jobs";
import { BboxMapView } from "../components/BboxMapPicker";
import { EChart, cssVar } from "../components/EChart";
import {
  Badge,
  Button,
  Dot,
  ErrorState,
  PageHeader,
  Panel,
  Row as LayoutRow,
  Skeleton,
  SkeletonRegion,
  Stack,
  Switch,
  Tag,
} from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Kpi } from "../components/foundation";
import { type Theme, useTheme } from "../lib/theme-store";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { useToast } from "../components/toast";
import { AnimatedNumber } from "../lib/motion";
import { useCanWrite } from "../auth/me";

// Per-job detail page: edit settings inline, browse every run this job has
// produced, and chart how throughput / duration / output have moved across runs.
// Reached from the Jobs table (job name -> this page).
export function JobDetail() {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const qc = useQueryClient();
  const notify = useToast();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  // Run-history "compare" picker: up to two run ids to diff on the compare page.
  const [compareSel, setCompareSel] = useState<string[]>([]);

  const jobQuery = useGetJobV1JobsJobIdGet(jobId, {
    query: { retry: false },
  });
  const runsQuery = useListRunsV1RunsGet(
    { job_id: jobId, limit: 200 },
    { query: { refetchInterval: 8000 } },
  );
  const schedulesQuery = useListSchedulesV1JobsJobIdSchedulesGet(jobId);

  const job = jobQuery.data as Job | undefined;
  // memoised: the `?? []` fallback is a new array each render, which defeats every memo
  // downstream that lists `runs` as a dependency
  const runs = useMemo(() => (runsQuery.data ?? []) as Run[], [runsQuery.data]);
  const schedules = schedulesQuery.data ?? [];
  useBreadcrumbTail([{ label: job?.name ?? jobId }]);

  // Per-run distinct-store counts (this-run sightings), for the run-history table,
  // separate from the progress `stores_found` catalog counter. Merchant runs only.
  // One batched request keyed by the run-id set (not one request per run).
  const statRunIds = useMemo(
    () => (job?.flow === "merchant_sweep" ? runs.filter((r) => r.run_id).map((r) => r.id) : []),
    [job?.flow, runs],
  );
  const statQuery = useQuery({
    queryKey: ["sweep-stats-batch", ...statRunIds],
    queryFn: () => runSweepStatsBatchV1RunsSweepStatsBatchPost({ run_ids: statRunIds }),
    enabled: statRunIds.length > 0,
    staleTime: 60_000,
  });
  const distinctByRun = useMemo(() => {
    const m = new Map<string, number>();
    for (const [id, n] of Object.entries(statQuery.data ?? {})) m.set(id, n);
    return m;
  }, [statQuery.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getGetJobV1JobsJobIdGetQueryKey(jobId) });
    qc.invalidateQueries({ queryKey: getListRunsV1RunsGetQueryKey() });
    qc.invalidateQueries({
      queryKey: getListSchedulesV1JobsJobIdSchedulesGetQueryKey(jobId),
    });
  };

  const enableToggle = useUpdateJobV1JobsJobIdPatch<Error>({
    mutation: {
      onSuccess: () => invalidate(),
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const runNow = useRunNowV1JobsJobIdRunsPost<Error>({
    mutation: {
      onSuccess: (run) => {
        notify("Run started");
        invalidate();
        navigate(`/runs/${run.id}`);
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const remove = useDeleteJobV1JobsJobIdDelete<Error>({
    mutation: {
      onSuccess: () => {
        notify("Job deleted");
        qc.invalidateQueries({ queryKey: getListJobsV1JobsGetQueryKey() });
        navigate("/jobs");
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const head = (
    <PageHeader
      className="mb-3.5"
      title={job?.name ?? "Job"}
      titleMeta={
        <>
          {job && <span className={`plat ${job.platform}`}>{platformLabel(job.platform)}</span>}
          {job && <Tag>{flowLabel(job.flow)}</Tag>}
          {job && !job.enabled && <Badge variant="outline">disabled</Badge>}
        </>
      }
      description={job ? `${flowLabel(job.flow)} on ${platformLabel(job.platform)}` : "Loading job…"}
      action={
        job && canWrite && (
          <LayoutRow gap="sm" className="flex-wrap justify-end">
            <Switch
              checked={job.enabled}
              disabled={enableToggle.isPending}
              onCheckedChange={() => enableToggle.mutate({ jobId, data: { enabled: !job.enabled } })}
              aria-label="toggle enabled"
              title={job.enabled ? "Disable job" : "Enable job"}
            />
            <Button
              variant="primary"
              disabled={!job.enabled}
              loading={runNow.isPending}
              onClick={() => runNow.mutate({ jobId })}
            >
              Run now
            </Button>
            <Button
              variant="destructive"
              loading={remove.isPending}
              onClick={() => {
                if (confirm(`Delete job "${job.name}"? This cannot be undone.`)) remove.mutate({ jobId });
              }}
            >
              Delete
            </Button>
          </LayoutRow>
        )
      }
    />
  );

  if (jobQuery.isError) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load this job"
              message={
                <>
                  Job <span className="mono">{jobId}</span> may have been deleted.
                </>
              }
              onRetry={() => jobQuery.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }
  if (!job) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body tight>
            <SkeletonRegion label="Loading job">
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      </>
    );
  }

  // Chart/KPI inputs: terminal runs carry a summary; order oldest->newest so the
  // charts read left (past) -> right (latest).
  const chrono = [...runs].reverse();
  const terminal = chrono.filter((r) => isTerminal(r.status));
  const succeeded = terminal.filter((r) => r.status === "succeeded").length;
  const successRate = terminal.length ? (succeeded / terminal.length) * 100 : null;

  const series = chrono.map((r) => ({
    run: r,
    ...runMetrics(r, job.flow as Flow),
  }));
  const lastTerminal = [...terminal].reverse()[0];
  const avg = (xs: (number | null)[]) => {
    const v = xs.filter((x): x is number => x != null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const avgRps = avg(series.map((s) => s.rps));
  const isMerchant = job.flow === "merchant_sweep";
  const outputLabel = isMerchant ? "stores found" : job.flow === "brand_sweep" ? "products" : "rows";
  const rpsUnit = isMerchant ? "probes/s" : "rows/s";

  // Every flow can be compared -- the compare page falls back to a counter diff for the
  // ones with no maps -- so the affordance is gated on having two started runs, not on the
  // flow. Only the merchant EXTRAS (the distinct-stores column, the maps) stay merchant-only.
  const canCompare = runs.filter((r) => r.run_id).length >= 2;

  // Toggle a run into the compare selection, keeping at most two (drop the oldest).
  const toggleCompare = (id: string) =>
    setCompareSel((sel) =>
      sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id].slice(-2),
    );
  const goCompare = () => {
    if (compareSel.length !== 2) return;
    const qs = new URLSearchParams({
      platform: job.platform,
      flow: job.flow,
      run_a: compareSel[0],
      run_b: compareSel[1],
    });
    navigate(`/compare?${qs}`);
  };

  return (
    <>
      {head}

      {/* at-a-glance KPIs */}
      <div className="grid g-4 mb-3.5">
        <Kpi label="total runs" value={<AnimatedNumber value={runs.length} format={fmtInt} />} foot={`${terminal.length} completed`} />
        <Kpi
          label="success rate"
          value={successRate == null ? "—" : <AnimatedNumber value={successRate} format={(n) => `${n.toFixed(0)}%`} />}
          tone={successRate == null ? undefined : successRate >= 80 ? "ok" : successRate >= 50 ? "warn" : "crit"}
          foot={`${succeeded}/${terminal.length} succeeded`}
        />
        <Kpi
          label="avg throughput"
          value={avgRps == null ? fmtAvg(avgRps, String) : <AnimatedNumber value={avgRps} format={(n) => Math.round(n).toLocaleString()} />}
          foot={rpsUnit}
        />
        <Kpi
          label="avg duration"
          value={fmtAvg(avg(series.map((s) => s.elapsed)), fmtDuration)}
          foot="per run"
        />
      </div>

      {/* trend charts across runs */}
      <div className="grid g-3 mb-3.5 items-start">
        <ChartPanel
          title="Throughput"
          metric="rps"
          series={series}
          rpsUnit={rpsUnit}
          outputUnit={outputLabel}
        />
        <ChartPanel
          title="Time taken"
          metric="elapsed"
          series={series}
          rpsUnit={rpsUnit}
          outputUnit={outputLabel}
        />
        <ChartPanel
          title={`Output · ${outputLabel}`}
          metric="output"
          series={series}
          rpsUnit={rpsUnit}
          outputUnit={outputLabel}
        />
      </div>

      {/* settings: read summary with inline edit */}
      <Panel className="mb-3.5">
        <Panel.Head title="Settings">
          <div className="flex-1" />
          {canWrite && !editing && (
            <Button size="sm" onClick={() => setEditing(true)}>Edit settings</Button>
          )}
        </Panel.Head>
        {editing ? (
          <JobForm
            job={job}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              invalidate();
              setEditing(false);
            }}
          />
        ) : (
          <Panel.Body tight>
            <SettingsSummary job={job} schedules={schedules} />
          </Panel.Body>
        )}
      </Panel>

      {/* run history */}
      <Panel>
        <Panel.Head title="Run history">
          <div className="flex-1" />
          {canCompare && (
            <Button
              size="sm"
              disabled={compareSel.length !== 2}
              onClick={goCompare}
              className="mr-2.5"
              style={{ fontSize: 11 }}
              title="Tick two runs to compare them"
            >
              Compare{compareSel.length ? ` (${compareSel.length}/2)` : " runs"}
            </Button>
          )}
          <span className="num faint" style={{ fontSize: 11 }}>{runs.length} runs</span>
        </Panel.Head>
        <Panel.Body tight>
          <table className="tbl">
            <thead>
              <tr>
                {canCompare && <th style={{ width: 28 }} title="compare" />}
                <th>Run</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Started</th>
                <th className="r">Duration</th>
                <th className="r">{rpsUnit}</th>
                <th className="r">{outputLabel}</th>
                {isMerchant && (
                  <th className="r" title="distinct stores this run (per-run sightings)">
                    distinct
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {[...series].reverse().map(({ run, rps, elapsed, output }) => (
                <tr key={run.id}>
                  {canCompare && (
                    <td>
                      <input
                        type="checkbox"
                        checked={compareSel.includes(run.id)}
                        disabled={!run.run_id || (compareSel.length >= 2 && !compareSel.includes(run.id))}
                        onChange={() => toggleCompare(run.id)}
                        title={run.run_id ? "select to compare" : "run never started"}
                      />
                    </td>
                  )}
                  <td>
                    <Link
                      className="mono"
                      to={`/runs/${run.id}`}
                      style={{
                        fontSize: 11.5,
                        ...(run.run_id ? {} : { fontStyle: "italic", color: "var(--ink-3)" }),
                      }}
                      title={run.run_id ? undefined : "This run was queued but a worker never started it, so it never got a run ID."}
                    >
                      {run.run_id ? `#${run.run_id}` : "not started"}
                    </Link>
                  </td>
                  <td><Badge variant={toneVariant(statusTone(run.status))} style={{ fontSize: 10.5 }}>{run.status}</Badge></td>
                  <td className="faint" style={{ fontSize: 11.5 }}>{run.trigger}</td>
                  <td className="faint" style={{ fontSize: 11.5 }}>
                    {fmtDateTime(run.started_at ?? run.created_at)} · {ago(run.started_at ?? run.created_at)}
                  </td>
                  <td className="r num">{fmtDuration(elapsed)}</td>
                  <td className="r num">{rps == null ? "—" : Math.round(rps).toLocaleString()}</td>
                  <td className="r num">{output == null ? "—" : fmtRows(output)}</td>
                  {isMerchant && (
                    <td className="r num">
                      {distinctByRun.has(run.id)
                        ? fmtInt(distinctByRun.get(run.id))
                        : run.run_id
                          ? "…"
                          : "—"}
                    </td>
                  )}
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={isMerchant ? 9 : 7}>
                    <div className="muted" style={{ textAlign: "center", padding: 18 }}>
                      No runs yet: start one with “Run now”.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Panel.Body>
      </Panel>

      {lastTerminal && (
        <p className="hint mt-2.5">
          Last completed run {ago(lastTerminal.finished_at ?? lastTerminal.created_at)}: view its full
          breakdown on the <Link to={`/runs/${lastTerminal.id}`}>run monitor</Link>.
        </p>
      )}
    </>
  );
}

// -- per-run derived metrics --------------------------------------------------
type RunMetric = {
  rps: number | null;
  elapsed: number | null;
  output: number | null;
};

function runMetrics(run: Run, flow: Flow): RunMetric {
  const stats = run.summary ?? run.progress ?? {};
  const elapsed = elapsedSeconds(run);
  const rps = flow === "merchant_sweep" ? probeThroughput(stats, elapsed) : throughput(stats, elapsed);
  const output =
    flow === "merchant_sweep"
      ? stats.stores_found ?? null
      : flow === "brand_sweep"
        ? stats.products_discovered ?? null
        : stats.rows_emitted ?? null;
  return { rps, elapsed, output };
}

// -- trend chart (Apache ECharts line) ----------------------------------------
const MAX_POINTS = 60;

type MetricKey = keyof RunMetric;
type MetricUnits = Record<MetricKey, string>;
type RunPoint = { run: Run; metrics: RunMetric };

// Row label + value formatting per metric, shared by the y axis, the panel footer and
// the tooltip (which lists all three metrics for the hovered run).
const METRIC_LABEL: Record<MetricKey, string> = {
  rps: "throughput",
  elapsed: "duration",
  output: "output",
};
const METRIC_FMT: Record<MetricKey, (n: number) => string> = {
  rps: (n) => Math.round(n).toLocaleString(),
  elapsed: fmtDuration,
  output: fmtRows,
};

function ChartPanel({
  title,
  metric,
  series,
  rpsUnit,
  outputUnit,
}: {
  title: string;
  metric: MetricKey;
  series: {
    run: Run;
    rps: number | null;
    elapsed: number | null;
    output: number | null;
  }[];
  rpsUnit: string;
  outputUnit: string;
}) {
  const points: RunPoint[] = useMemo(
    () =>
      series.slice(-MAX_POINTS).map(({ run, rps, elapsed, output }) => ({
        run,
        metrics: { rps, elapsed, output },
      })),
    [series],
  );
  const fmt = METRIC_FMT[metric];
  const vals = points.map((p) => p.metrics[metric]).filter((v): v is number => v != null);
  const latest = vals.length ? vals[vals.length - 1] : null;
  const peak = vals.length ? Math.max(...vals) : null;
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;

  const { theme } = useTheme();
  const option = useMemo(
    () => lineOption(points, metric, { rps: rpsUnit, elapsed: "", output: outputUnit }, theme),
    [points, metric, rpsUnit, outputUnit, theme],
  );

  return (
    <Panel>
      <Panel.Head title={title}>
        <div className="flex-1" />
        <span className="num faint" style={{ fontSize: 11 }}>
          latest {latest == null ? "—" : fmt(latest)}
        </span>
      </Panel.Head>
      <Panel.Body>
        {vals.length === 0 ? (
          <div className="muted" style={{ padding: "20px 0", textAlign: "center", fontSize: 12 }}>
            No completed runs yet.
          </div>
        ) : (
          <>
            <EChart option={option} height={172} />
            <div
              className="row"
              style={{
                justifyContent: "space-between",
                marginTop: 8,
                fontSize: 10.5,
              }}
            >
              <span className="faint">
                avg <b className="num text-ink-2">{avg == null ? "—" : fmt(avg)}</b>
              </span>
              <span className="faint">
                peak <b className="num text-ink-2">{peak == null ? "—" : fmt(peak)}</b>
              </span>
              <span className="faint num">
                {vals.length} runs
                {series.length > MAX_POINTS ? ` (last ${MAX_POINTS})` : ""}
              </span>
            </div>
          </>
        )}
      </Panel.Body>
    </Panel>
  );
}

function statusColor(tone: string): string {
  if (tone === "idle") return cssVar("--line-2", "#c9c9c9");
  return cssVar(`--${tone}`, "#888");
}

// The tooltip is raw HTML, and a run's error text is scraper output we do not control.
function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

// When a run started, as the x axis writes it: date on one line, IST clock on the next.
function axisStamp(run: Run): string {
  const d = parseTs(run.started_at ?? run.created_at);
  const day = d.toLocaleDateString("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
  });
  const clock = d.toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${day}\n${clock}`;
}

// Build the ECharts option for one metric line over runs (oldest -> newest). Runs are
// evenly spaced (a category axis, not a time axis) so run-to-run movement stays readable
// when the schedule is irregular; the axis is labelled with each run's start time, thinned
// to ~4 stamps anchored on the newest run. Y axis auto-scales to the data; null runs
// (queued / running, no metric yet) become gaps; each point is status-coloured; a dashed
// mark-line shows the average; the tooltip reports every metric for the hovered run.
// `_theme` is a real input even though the body never reads it: the option is built from
// resolved CSS vars, so it has to be rebuilt when the theme flips.
function lineOption(
  points: RunPoint[],
  metric: MetricKey,
  units: MetricUnits,
  _theme: Theme,
): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const brand = cssVar("--brand", "#e0a325");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");

  // Category values must stay unique (duplicate names confuse the axis pointer), so the
  // run id remains the key and the visible stamp comes from the label formatter.
  const labels = points.map((p) => p.run.id);
  const data = points.map((p) =>
    p.metrics[metric] == null
      ? { value: null as number | null }
      : {
          value: p.metrics[metric],
          itemStyle: { color: statusColor(statusTone(p.run.status)) },
        },
  );
  const last = points.length - 1;
  const tickEvery = Math.max(1, Math.ceil(points.length / 4));

  return {
    // `containLabel` reserves room for the y labels but not for the first/last x stamp,
    // which `boundaryGap: false` parks half-off each end of the plot: hence the gutters.
    grid: { left: 20, right: 26, top: 14, bottom: 4, containLabel: true },
    tooltip: {
      trigger: "axis",
      // Panels clip their overflow, so a tooltip anchored in the chart box gets cut off at
      // the panel edge. Hanging it off <body> lets ECharts keep it inside the viewport.
      appendTo: () => document.body,
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [7, 10],
      extraCssText: "box-shadow: 0 4px 16px rgba(0,0,0,.12); z-index: 60;",
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
      formatter: (raw: unknown) => {
        const arr = raw as { dataIndex: number }[];
        const p = points[arr[0]?.dataIndex];
        if (!p) return "";
        const dot = `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${statusColor(
          statusTone(p.run.status),
        )};margin-right:5px"></span>`;
        const rows = (Object.keys(METRIC_LABEL) as MetricKey[]).map((k) => {
          const v = p.metrics[k];
          const text = v == null ? "—" : `${METRIC_FMT[k](v)}${units[k] ? ` ${units[k]}` : ""}`;
          const cell = k === metric ? `<b>${text}</b>` : text;
          return `<tr><td style="opacity:.7;padding-right:12px">${METRIC_LABEL[k]}</td><td style="text-align:right">${cell}</td></tr>`;
        });
        const failed = p.run.status === "failed" && p.run.error;
        return [
          `<div>${fmtDateTime(p.run.started_at ?? p.run.created_at)}</div>`,
          `<div style="margin:2px 0 5px">${dot}${p.run.status} <span style="opacity:.55">· ${p.run.trigger}</span></div>`,
          `<table style="border-collapse:collapse">${rows.join("")}</table>`,
          failed
            ? `<div style="margin-top:5px;max-width:230px;white-space:normal;opacity:.7">${escapeHtml(
                p.run.error!,
              ).slice(0, 140)}</div>`
            : "",
        ].join("");
      },
    },
    xAxis: {
      type: "category",
      data: labels,
      boundaryGap: false,
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: {
        color: faint,
        fontSize: 9,
        fontFamily: mono,
        lineHeight: 11,
        margin: 7,
        // Thin from the right so the newest run is always stamped.
        interval: (i: number) => (last - i) % tickEvery === 0,
        formatter: (_v: string, i: number) => (points[i] ? axisStamp(points[i].run) : ""),
      },
    },
    yAxis: {
      type: "value",
      scale: true,
      splitNumber: 3,
      axisLabel: {
        color: faint,
        fontSize: 9,
        fontFamily: mono,
        formatter: (v: number) => METRIC_FMT[metric](v),
      },
      splitLine: { lineStyle: { color: line, type: "dashed" } },
    },
    series: [
      {
        type: "line",
        data,
        connectNulls: false,
        showSymbol: true,
        symbolSize: 6,
        smooth: false,
        lineStyle: { color: brand, width: 1.75 },
        itemStyle: { color: brand, borderColor: bg, borderWidth: 1 },
        areaStyle: { color: brand, opacity: 0.08 },
        emphasis: { scale: 1.5 },
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { color: faint, type: "dashed", width: 1 },
          label: { show: false },
          data: [{ type: "average" }],
        },
      },
    ],
  };
}

// -- settings read summary ----------------------------------------------------
// A scope selector: its "all" literal, or the names it narrows to with their count.
function ScopeValue({ scope }: { scope: string | string[] }) {
  if (!Array.isArray(scope)) return <span className="mono">{scope}</span>;
  return (
    <span>
      {scope.join(", ")} <span className="faint">({scope.length})</span>
    </span>
  );
}

function SettingsSummary({ job, schedules }: { job: Job; schedules: ScheduleOut[] }) {
  const isMerchant = job.flow === "merchant_sweep";
  const isCartLike = job.flow === "cart_inventory" || job.flow === "store_sku_map";
  // A job stores exactly the scope keys its flow reads, so one row per stored key shows
  // every axis the run narrows by and none it lacks (SKU match has no store axis).
  const { brand_scope, candidate_scope, keyword_scope, category_scope, store_scope } = job.inputs;
  // force_refresh / include_bundles read as booleans, not tuning numbers -- they get their
  // own explicit rows below instead of being dumped into the generic knob string.
  const knobEntries = Object.entries(job.knobs ?? {}).filter(
    ([k]) =>
      k !== "bbox" && k !== "force_refresh" && k !== "include_bundles" && k !== "include_inactive" && k !== "skip_existing",
  );
  const isShelfIds = job.flow === "shelf_id_resolve";
  const bbox = Array.isArray(job.knobs?.bbox) ? (job.knobs.bbox as number[]) : null;
  return (
    <>
    <table className="tbl">
      <tbody>
        <Row k="Platform" v={platformLabel(job.platform)} />
        <Row k="Flow" v={flowLabel(job.flow)} />
        {isMerchant ? (
          <>
            <Row
              k="Force refresh"
              v={job.knobs?.force_refresh ? "Yes: bypass serviceability cache" : "No: use cache"}
            />
            <Row
              k="Region scope"
              v={bbox ? <span className="mono">{bbox.join(", ")}</span> : <span className="faint">All of India</span>}
            />
          </>
        ) : (
          <>
            {brand_scope !== undefined && <Row k="Brand scope" v={<ScopeValue scope={brand_scope} />} />}
            {candidate_scope !== undefined && (
              <Row k="Search terms" v={<ScopeValue scope={candidate_scope} />} />
            )}
            {keyword_scope !== undefined && <Row k="Keywords" v={<ScopeValue scope={keyword_scope} />} />}
            {category_scope !== undefined && (
              <Row k="Categories" v={<ScopeValue scope={category_scope} />} />
            )}
            {store_scope !== undefined && <Row k="Store scope" v={<ScopeValue scope={store_scope} />} />}
          </>
        )}
        {/* Whether the pass re-buys what it already knows is the only choice this job
            offers, and it is normally UNSET -- so dumping it into the generic knob string
            below would show nothing at all for the common case and leave the job page
            unable to say what the job does. */}
        {isShelfIds && (
          <Row
            k="Existing pairs"
            v={
              job.knobs?.skip_existing === false ? (
                <span>
                  <strong>Full re-fetch</strong> - every store x SKU pair is asked again, and
                  an id that changed is reported
                </span>
              ) : (
                <span>
                  Skipped - only pairs with no answer yet{" "}
                  <span className="faint">(default)</span>
                </span>
              )
            }
          />
        )}
        {job.flow === "cart_inventory" && (
          <Row
            k="Inactive coverage"
            v={job.knobs?.include_inactive === true
              ? "On - inactive/disabled SKUs at every active store; matched and carriage settings bypassed"
              : "Off - active SKU selection and saved carriage settings apply"}
          />
        )}
        {isCartLike && (
          <Row
            k="Include combo SKUs"
            v={
              job.knobs?.include_bundles
                ? "Yes - multipacks/combos are probed too"
                : "No - combos are skipped (default)"
            }
          />
        )}
        <Row
          k="Advanced knobs"
          v={
            knobEntries.length === 0 ? (
              <span className="faint">engine defaults</span>
            ) : (
              <span className="mono" style={{ fontSize: 11.5 }}>
                {knobEntries.map(([k, v]) => `${k}=${String(v)}`).join(" · ")}
              </span>
            )
          }
        />
        <Row
          k="Schedules"
          v={
            schedules.length === 0 ? (
              <span className="faint">none: runs manually</span>
            ) : (
              <span className="col" style={{ gap: 4 }}>
                {schedules.map((s) => (
                  <span key={s.id} className="row" style={{ gap: 8, alignItems: "center" }}>
                    <Dot tone={s.enabled ? "ok" : undefined} />
                    <span style={{ fontSize: 12.5 }}>{humanizeCron(s.cron)}</span>
                    <code className="faint mono" style={{ fontSize: 10.5 }}>{s.timezone}</code>
                  </span>
                ))}
              </span>
            )
          }
        />
      </tbody>
    </table>
    {isMerchant && (
      <div className="col" style={{ gap: 6, marginTop: 12 }}>
        <BboxMapView bbox={bbox} height={260} />
        <span className="hint">
          {bbox
            ? "Region scope, the merchant sweep is bounded to this box."
            : "No region scope set, the sweep covers all of India (box shown for reference)."}
        </span>
      </div>
    )}
    </>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <tr>
      <td className="faint" style={{ whiteSpace: "nowrap", width: 1, paddingRight: 18 }}>{k}</td>
      <td className="strong" style={{ fontSize: 12.5 }}>{v}</td>
    </tr>
  );
}

function fmtAvg(n: number | null, fmt: (n: number) => string): string {
  return n == null ? "—" : fmt(n);
}
